// Adaptador mock de GET /reports/quick/daily (contrato v2) mientras el backend
// no exista. Determinista: cada (unidad, día) sale de su propia semilla, así que
// el mismo día da los mismos números aunque cambie el periodo pedido. Zona
// Monterrey / Saltillo para que geocercas y direcciones se lean creíbles.
//
// Arma primero los TRAMOS del día (como la bitácora, ESPECIFICACION.txt §1.5) y
// de ellos deriva viajes, paradas, geocercas y totales: una sola medición para
// todos los informes rápidos, igual que el backend.
import type {
  DailyUnitSummary,
  Place,
  QuickAlert,
  QuickDailyParams,
  QuickDailyResponse,
  QuickDriver,
  QuickFuel,
  QuickStop,
  QuickTrip,
  Segment,
  SegmentState,
} from "./types";
import { daysBetween } from "./quickReports.compute";

// shortcut: México sin horario de verano desde 2022 → UTC-6 fijo; sólo vale
// para el mock con tz America/Mexico_City.
const TZ_OFFSET_MS = 6 * 3600_000;
const MIN = 60_000;
const OVERSPEED_KMH = 90;

const mulberry32 = (seed: number) => () => {
  let t = (seed += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
const hash = (s: string) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
};

// Tipo de geocerca: los de patio (almacén, mantenimiento, patio de camiones,
// patio) son donde moverse a ≤ 15 km/h cuenta como maniobra.
const GF_TYPE: Record<string, string> = {
  "gf-cedis-apodaca": "almacén",
  "gf-cliente-stacat": "cliente",
  "gf-patio-garcia": "patio de camiones",
  "gf-planta-ramos": "cliente",
  "gf-patio-saltillo": "patio",
  "gf-cliente-escobedo": "cliente",
  "gf-taller-guadalupe": "mantenimiento",
};
const YARD_TYPES = new Set(["almacén", "mantenimiento", "patio de camiones", "patio"]);
const isYard = (p: Place) => !!p.geofence && YARD_TYPES.has(GF_TYPE[p.geofence.id] ?? "");

// Tres lugares sin `address`, como una parada que el backend todavía no
// geocodifica: la tabla de Paradas debe mostrar "—" y las coordenadas igual.
const PLACES: Place[] = [
  { lat: 25.7804, lng: -100.1882, address: "Carretera Miguel Alemán km 21, Apodaca, N.L.", geofence: { id: "gf-cedis-apodaca", name: "CEDIS Apodaca" } },
  { lat: 25.6866, lng: -100.3161 },
  { lat: 25.6714, lng: -100.4598, address: "Blvd. Díaz Ordaz 200, Santa Catarina, N.L.", geofence: { id: "gf-cliente-stacat", name: "Cliente Santa Catarina" } },
  { lat: 25.7489, lng: -100.2939 },
  { lat: 25.6529, lng: -100.2766, address: "Av. Lázaro Cárdenas 2400, San Pedro Garza García, N.L." },
  { lat: 25.8001, lng: -100.5868, address: "Libramiento Noreste km 5, García, N.L.", geofence: { id: "gf-patio-garcia", name: "Patio García" } },
  { lat: 25.6217, lng: -100.9714 },
  { lat: 25.5357, lng: -100.9447, address: "Parque Industrial Santa María, Ramos Arizpe, Coah.", geofence: { id: "gf-planta-ramos", name: "Planta Ramos Arizpe" } },
  { lat: 25.4383, lng: -100.9737, address: "Blvd. V. Carranza 3500, Saltillo, Coah." },
  { lat: 25.4232, lng: -101.0053, address: "Periférico Luis Echeverría 1200, Saltillo, Coah.", geofence: { id: "gf-patio-saltillo", name: "Patio Saltillo" } },
  { lat: 25.7322, lng: -100.2087, address: "Av. Miguel Alemán 5400, Guadalupe, N.L.", geofence: { id: "gf-taller-guadalupe", name: "Taller Guadalupe" } },
  { lat: 25.8649, lng: -100.2366, address: "Carretera a Laredo km 18, Escobedo, N.L.", geofence: { id: "gf-cliente-escobedo", name: "Cliente Escobedo" } },
];
const YARDS = PLACES.filter(isYard);

const UNIT_PREFIX = ["Tracto", "Torton", "Rabón", "Camioneta"];
const DRIVERS = [
  "Juan Carlos Treviño", "María Fernanda Garza", "José Luis Cantú", "Roberto Villarreal", "Ana Sofía Elizondo",
  "Héctor Salinas", "Luis Ángel Rodríguez", "Miguel Ángel Leal", "Claudia Martínez", "Francisco Javier Ortiz",
];

type MockUnit = { device_id: string; unit_name: string; plate: string };

export const mockUnits = (companyId: string, count = 18): MockUnit[] => {
  const rnd = mulberry32(hash(`units-${companyId}`));
  return Array.from({ length: count }, (_, i) => ({
    device_id: `mock-dev-${String(i + 1).padStart(2, "0")}`,
    unit_name: `${UNIT_PREFIX[i % UNIT_PREFIX.length]} ${String(101 + i)}`,
    plate: `${String.fromCharCode(65 + Math.floor(rnd() * 26))}${String.fromCharCode(65 + Math.floor(rnd() * 26))}-${100 + Math.floor(rnd() * 900)}-${String.fromCharCode(65 + Math.floor(rnd() * 26))}`,
  }));
};

const distKm = (a: Place, b: Place) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  // 1.3: factor de camino sobre la línea recta.
  return 2 * R * Math.asin(Math.sqrt(h)) * 1.3;
};

const round1 = (n: number) => Math.round(n * 10) / 10;
const round2 = (n: number) => Math.round(n * 100) / 100;

// Conductor y tanque son de la unidad, no del día.
const unitDriver = (u: MockUnit): QuickDriver | null => {
  const h = hash(`drv-${u.device_id}`);
  if (h % 10 >= 7) return null; // ~30% sin conductor asignado
  const name = DRIVERS[h % DRIVERS.length];
  return {
    id: `drv-${h % 1000}`,
    name,
    license_number: `NL${String(h % 10_000_000).padStart(7, "0")}`,
    license_expires_at: `${2026 + (h % 4)}-${String(1 + (h % 12)).padStart(2, "0")}-${String(1 + (h % 28)).padStart(2, "0")}`,
    photo_url: null,
    shift_hours: 8 + (h % 5),
    shift_days: 5 + (h % 2),
  };
};
// ~30% con sensor de combustible; una de ellas sin capacidad de tanque (litros "—").
const unitTank = (u: MockUnit): { fuel: boolean; tank: number | null } => {
  const h = hash(`fuel-${u.device_id}`);
  if (h % 10 >= 3) return { fuel: false, tank: null };
  return { fuel: true, tank: h % 10 === 0 ? null : 300 + (h % 4) * 100 };
};

const emptyCounts = () => ({ overspeed: 0, overspeed_max_kmh: 0, harsh_brake: 0, harsh_accel: 0, panic: 0, jamming: 0, camera: 0 });

export const mockDay = (unit: MockUnit, companyId: string, date: string, now: number): DailyUnitSummary => {
  const rnd = mulberry32(hash(`${unit.device_id}|${date}`));
  const [y, m, d] = date.split("-").map(Number);
  const dayStart = Date.UTC(y, m - 1, d) + TZ_OFFSET_MS;
  const dayEnd = Math.min(dayStart + 86_400_000, now);
  const partial = now < dayStart + 86_400_000;
  const weekend = new Date(Date.UTC(y, m - 1, d)).getUTCDay() % 6 === 0;
  const driver = unitDriver(unit);
  const { fuel: hasFuel, tank } = unitTank(unit);

  const base = {
    device_id: unit.device_id,
    vehicle_id: null,
    company_id: companyId,
    unit_name: unit.unit_name,
    plate: unit.plate,
    date,
    tz: "America/Mexico_City",
    partial,
    driver,
    tank_capacity_l: tank,
    camera_events: [] as { ts: number; type: string }[],
    generated_at: now,
  };

  // ~4% de los días la unidad no reporta nada (bitácora vacía).
  if (rnd() < 0.04 || dayEnd <= dayStart) {
    return {
      ...base,
      distance_km: 0, driving_s: 0, idle_s: 0, stopped_s: 0, maneuver_s: 0, rest_s: 0, no_data_s: 0,
      max_speed_kmh: 0, avg_speed_kmh: 0, stops_count: 0,
      overspeed: { limit_kmh: OVERSPEED_KMH, count: 0, max_kmh: 0, duration_s: 0 },
      hourly_km: Array(24).fill(0), trips: [], stops: [], geofences: [], alerts: [], segments: [],
      fuel: null, event_counts: emptyCounts(), first_fix: null, last_fix: null, points_count: 0,
    };
  }

  // ~12% de las unidades no se mueven en el día (más en fin de semana).
  const parked = rnd() < (weekend ? 0.45 : 0.12);
  const segs: Segment[] = [];
  const route: { from: Place; to: Place }[] = [];
  let t = dayStart;
  let here = rnd() < 0.6 ? YARDS[Math.floor(rnd() * YARDS.length)] : PLACES[Math.floor(rnd() * PLACES.length)];

  // Agrega un tramo [t, t+dur) recortado al fin del día. Devuelve false si ya no cabe.
  const push = (state: SegmentState, durMs: number, from: Place, to = from, km = 0, avg = 0, max = 0) => {
    if (t >= dayEnd) return false;
    if (durMs <= 0) return true;
    // Bordes en segundos enteros: la suma de los tramos de un día completo da 86 400 s exactos.
    const end = Math.min(Math.round((t + durMs) / 1000) * 1000, dayEnd);
    if (end <= t) return true;
    // Parada recortada por el fin del día (o por "ahora" en Hoy): se decide con
    // lo que duró de verdad, como la bitácora (§1.5 paso 4).
    if (state === "rest" || state === "stopped") state = end - t >= 30 * MIN ? "rest" : "stopped";
    const frac = (end - t) / durMs || 0;
    const moving = state === "driving" || state === "maneuver";
    const g = state === "no_data" ? null : !moving || state === "maneuver" ? from.geofence : null;
    segs.push({
      state,
      start_ts: t,
      end_ts: end,
      duration_s: Math.round((end - t) / 1000),
      geofence: g ? { ...g, type: GF_TYPE[g.id] ?? null } : null,
      distance_km: round2(km * frac),
      avg_speed_kmh: avg,
      max_speed_kmh: max,
      // Como el backend: dirección sólo donde se detuvo (Detenido / Descanso).
      start:
        state === "no_data"
          ? null
          : { lat: from.lat, lng: from.lng, ...(state === "stopped" || state === "rest" ? { address: from.address ?? null } : {}) },
      end: state === "no_data" ? null : { lat: to.lat, lng: to.lng },
    });
    route.push({ from, to });
    t = end;
    return t < dayEnd;
  };
  // Parada: 30 min o más con motor apagado es descanso (§1.4).
  const halt = (durMs: number, at: Place) => push(durMs >= 30 * MIN ? "rest" : "stopped", durMs, at);

  // Madrugada: estacionada; en ~35% de los días un hueco sin reportar.
  const workStart = dayStart + (5.5 + rnd() * 3) * 3600_000;
  if (!parked && rnd() < 0.35) {
    const gapAt = dayStart + (1 + rnd() * 2) * 3600_000;
    halt(gapAt - t, here);
    push("no_data", (1 + rnd() * 2) * 3600_000, here);
  }

  const trips = parked ? 0 : 2 + Math.floor(rnd() * 5);
  let alive = halt((parked ? dayEnd : workStart) - t, here);
  for (let i = 0; i < trips && alive; i++) {
    let to = PLACES[Math.floor(rnd() * PLACES.length)];
    if (to === here) to = PLACES[(PLACES.indexOf(here) + 3) % PLACES.length];
    // Calienta motor y, si está en patio, maniobra para salir.
    alive = push("idle", (1 + rnd() * 6) * MIN, here);
    if (alive && isYard(here)) {
      const km = 0.3 + rnd() * 1.2;
      const avg = 7 + Math.round(rnd() * 5);
      alive = push("maneuver", (km / avg) * 3600_000, here, here, km, avg, Math.min(15, avg + 3 + Math.round(rnd() * 4)));
    }
    if (!alive) break;
    const km = distKm(here, to) * (0.95 + rnd() * 0.2);
    const avg = Math.round(38 + rnd() * 30);
    alive = push("driving", (km / avg) * 3600_000, here, to, km, avg, Math.round(avg + 20 + rnd() * 45));
    here = to;
    if (alive && isYard(here)) {
      const mkm = 0.2 + rnd() * 0.8;
      const mavg = 6 + Math.round(rnd() * 5);
      alive = push("maneuver", (mkm / mavg) * 3600_000, here, here, mkm, mavg, Math.min(15, mavg + 4));
    }
    if (alive) alive = push("idle", (1 + rnd() * 8) * MIN, here);
    if (!alive) break;
    const last = i === trips - 1;
    alive = halt(last ? dayEnd - t : (8 + rnd() * (rnd() < 0.3 ? 150 : 45)) * MIN, here);
  }
  if (t < dayEnd) halt(dayEnd - t, here);

  // ---------- Derivados de los tramos ----------
  const tripsOut: QuickTrip[] = [];
  const stops: QuickStop[] = [];
  const hourly_km = Array(24).fill(0) as number[];
  const totals = { driving: 0, maneuver: 0, idle: 0, stopped: 0, rest: 0, no_data: 0 };
  let km = 0;
  let overCount = 0;
  let overMax = 0;
  let overDur = 0;
  // Como el backend (dailyUnitSummary.estimatedDriving): la mitad de los huecos
  // del mock son de una unidad que siguió avanzando sin reportar.
  for (const s of segs) {
    if (s.state === "no_data" && rnd() < 0.5) {
      const kmh = 40 + Math.round(rnd() * 30);
      Object.assign(s, { state: "driving", estimated: true, distance_km: round2((s.duration_s / 3600) * kmh), avg_speed_kmh: kmh, max_speed_kmh: 0 });
    }
  }
  segs.forEach((s, i) => {
    totals[s.state] += s.duration_s;
    km += s.distance_km;
    if (s.state === "driving") {
      tripsOut.push({ start_ts: s.start_ts, end_ts: s.end_ts, distance_km: s.distance_km, max_speed_kmh: s.max_speed_kmh, from: route[i].from, to: route[i].to });
      if (s.max_speed_kmh > OVERSPEED_KMH) {
        const n = 1 + Math.floor(rnd() * 3);
        overCount += n;
        overMax = Math.max(overMax, s.max_speed_kmh);
        overDur += n * Math.round(30 + rnd() * 150);
      }
    }
    if (s.state === "stopped" || s.state === "rest") stops.push({ start_ts: s.start_ts, end_ts: s.end_ts, duration_s: s.duration_s, place: route[i].from });
    if ((s.state === "driving" || s.state === "maneuver") && s.end_ts > s.start_ts) {
      for (let h = 0; h < 24; h++) {
        const hs = dayStart + h * 3600_000;
        const overlap = Math.max(0, Math.min(s.end_ts, hs + 3600_000) - Math.max(s.start_ts, hs));
        if (overlap > 0) hourly_km[h] += (s.distance_km * overlap) / (s.end_ts - s.start_ts);
      }
    }
  });

  // Estadías en geocerca: tramos seguidos en la misma geocerca (cruzarla en marcha no cuenta).
  const geofences: DailyUnitSummary["geofences"] = [];
  for (let i = 0; i < segs.length; i++) {
    const g = segs[i].geofence;
    if (!g) continue;
    let j = i;
    while (j + 1 < segs.length && segs[j + 1].geofence?.id === g.id) j++;
    const enter = segs[i].start_ts;
    const exit = segs[j].end_ts;
    geofences.push({
      id: g.id,
      name: g.name,
      enter_ts: enter <= dayStart ? null : enter,
      exit_ts: exit >= dayEnd ? null : exit,
      duration_s: Math.round((exit - enter) / 1000),
    });
    i = j;
  }

  const moved = tripsOut.length > 0;
  const event_counts = {
    ...emptyCounts(),
    overspeed: overCount,
    overspeed_max_kmh: overMax,
    harsh_brake: moved ? Math.floor(rnd() * 3.4) : 0,
    harsh_accel: moved ? Math.floor(rnd() * 2.6) : 0,
    panic: rnd() < 0.03 ? 1 : 0,
    jamming: rnd() < 0.05 ? 1 : 0,
  };

  const lastPlace = route[route.length - 1]?.to ?? here;
  const alerts: QuickAlert[] = [];
  const at = () => dayStart + rnd() * (dayEnd - dayStart);
  if (event_counts.panic) alerts.push({ ts: at(), code: 1, type: "sos", place: lastPlace });
  if (rnd() < 0.08) {
    const ts = at();
    alerts.push({ ts, code: 23, type: "power_disconnection", place: lastPlace });
    alerts.push({ ts: ts + 20 * MIN, code: 22, type: "power_connection", place: lastPlace });
  }
  if (event_counts.jamming) alerts.push({ ts: at(), code: 36, type: "jamming", place: lastPlace });
  if (totals.no_data && rnd() < 0.3) alerts.push({ ts: segs.find((s) => s.state === "no_data")!.start_ts, code: 131, type: "gps_lost", place: null });
  alerts.sort((a, b) => a.ts - b.ts);

  // Combustible: ~2.6 km/L de tracto; carga si queda por debajo de 25%.
  let fuel: QuickFuel | null = null;
  if (hasFuel) {
    const cap = tank ?? 400;
    const start = 40 + Math.round(rnd() * 55);
    const consumed = ((km / (2.3 + rnd() * 0.8)) / cap) * 100;
    const refueled = start - consumed < 25 ? Math.round(45 + rnd() * 20) : 0;
    const end = Math.max(0, Math.min(100, Math.round(start - consumed + refueled)));
    const consumed_l = tank ? round1((consumed / 100) * tank) : null;
    fuel = {
      start_pct: start,
      end_pct: end,
      consumed_pct: round1(consumed),
      refueled_pct: refueled,
      consumed_l,
      refueled_l: tank ? round1((refueled / 100) * tank) : null,
      km_per_l: consumed_l && consumed_l > 0 ? round2(km / consumed_l) : null,
    };
  }

  const distance_km = round2(km);
  const odoBase = 120_000 + (hash(unit.device_id) % 300_000);
  const firstMove = segs.find((s) => s.start);
  const lastMove = [...segs].reverse().find((s) => s.end);
  return {
    ...base,
    distance_km,
    odo_start_km: odoBase,
    odo_end_km: round1(odoBase + distance_km),
    driving_s: totals.driving,
    maneuver_s: totals.maneuver,
    idle_s: totals.idle,
    stopped_s: totals.stopped,
    rest_s: totals.rest,
    no_data_s: totals.no_data,
    max_speed_kmh: Math.max(0, ...segs.map((s) => s.max_speed_kmh)),
    avg_speed_kmh: totals.driving ? Math.round(distance_km / (totals.driving / 3600)) : 0,
    stops_count: stops.length,
    overspeed: { limit_kmh: OVERSPEED_KMH, count: overCount, max_kmh: overMax, duration_s: overDur },
    hourly_km: hourly_km.map(round1),
    segments: segs,
    trips: tripsOut,
    stops,
    geofences,
    alerts,
    fuel,
    event_counts,
    first_fix: firstMove?.start ? { ts: firstMove.start_ts, ...firstMove.start } : null,
    last_fix: lastMove?.end ? { ts: lastMove.end_ts, ...lastMove.end } : null,
    points_count: Math.round((dayEnd - dayStart - totals.no_data * 1000) / 30_000),
  };
};

// Una unidad que el mock no conoce (p. ej. un equipo real abierto desde el mapa)
// recibe datos inventados con su propio id, para poder probar la bitácora ahí.
const unitFor = (id: string, known: MockUnit[]): MockUnit =>
  known.find((u) => u.device_id === id) ?? { device_id: id, unit_name: `Unidad ${id.slice(-4).toUpperCase()}`, plate: "—" };

export const mockQuickDaily = (params: QuickDailyParams, now = Date.now()): QuickDailyResponse => {
  const known = mockUnits(params.companyId);
  const units = params.deviceIds?.length ? params.deviceIds.map((id) => unitFor(id, known)) : known;
  const days = daysBetween(params.from, params.to);
  // Como el backend: tramos, viajes y paradas sólo cuando se pide un día; los
  // totales (y stops_count) vienen siempre.
  const omit = params.from !== params.to;
  return {
    from: params.from,
    to: params.to,
    tz: params.tz,
    generated_at: now,
    items: days.flatMap((date) =>
      units.map((u) => {
        const d = mockDay(u, params.companyId, date, now);
        return omit ? { ...d, segments: [], trips: [], stops: [], segments_omitted: true } : d;
      }),
    ),
    mock: true,
  };
};
